import { useCallback, useMemo } from "react";
import { AVAILABLE_MAPS, AVAILABLE_WINGMAN_MAPS, formatMapName } from "../../constants/maps";
import { getMapMetaById } from "../../constants/mapMeta";
import { TIERS } from "../../constants/membership";
import {
    buildWorkshopItemUrl,
    normalizeLocationId,
    normalizeMapSelectionToMapId,
    summarizeRecapItems,
} from "./lobbyPageUtils";
import {
    resolveLobbyLocationsToDisplay,
    resolveLobbyMapsToDisplay,
    resolveSelectionOwnerTier,
} from "./lobbyEffectiveSelections";

export default function useLobbyViewModel({
    catalog,
    user,
    currentLobby,
    currentLobbyCode,
    matchmakingOptions,
    lobbyMapsSelected,
    lobbyWingmanMapsSelected,
    selectedServerLocations,
    lobbyState,
    matchmakingStarted,
    friends,
}) {
    const currentLobbyData =
        currentLobbyCode && currentLobby ? currentLobby : null;
    const isLeader = user ? currentLobbyData?.hostId === user.userId : false;
    const lobbyMembers =
        currentLobbyData && Array.isArray(currentLobbyData.members)
            ? currentLobbyData.members
            : [];
    const selfMember = user
        ? lobbyMembers.find((m) => m.userId === user.userId)
        : null;
    const hostMember = currentLobbyData
        ? lobbyMembers.find(
              (m) =>
                  m.userId === currentLobbyData.hostId || m.role === "host",
          )
        : null;
    const otherMembers = user
        ? lobbyMembers.filter((m) => m.userId !== user.userId).slice(0, 4)
        : [];

    const isLobbyFull =
        currentLobbyData?.members?.length >= currentLobbyData?.maxPlayers || false;
    const canEditLobbySettings = user
        ? currentLobbyData?.hostId === user.userId
        : false;
    const lobbyOptionsToDisplay = !currentLobbyCode
        ? matchmakingOptions
        : currentLobbyData?.lobbyOptions || {};
    const selectionOwnerTier = resolveSelectionOwnerTier({
        currentLobbyCode,
        currentLobbyData,
        viewerTier: catalog?.membership?.tier || TIERS.FREE,
    });
    const enabledServerLocationIds = Array.isArray(catalog.serverLocations)
        ? catalog.serverLocations
            .map((location) => normalizeLocationId(location.id))
            .filter(Boolean)
        : [];
    const queueMode = currentLobbyData?.queueMode === "wingman" ? "wingman" : "standard";
    const isWingmanQueue = queueMode === "wingman";
    const lobbyMapsToDisplay = resolveLobbyMapsToDisplay({
        currentLobbyCode,
        personalMaps: isWingmanQueue ? lobbyWingmanMapsSelected : lobbyMapsSelected,
        lobbyMaps: isWingmanQueue
            ? currentLobbyData?.lobbyWingmanMapsSelected
            : currentLobbyData?.lobbyMapsSelected,
        selectionOwnerTier,
        queueMode,
    });
    const lobbyServerLocationsToDisplay = resolveLobbyLocationsToDisplay({
        currentLobbyCode,
        personalLocations: selectedServerLocations,
        lobbyLocations: currentLobbyData?.lobbyServerLocationsSelected,
        enabledLocationIds: enabledServerLocationIds,
        selectionOwnerTier,
    });
    const hasSelectedMaps = (lobbyMapsToDisplay?.length || 0) > 0;
    const hasSelectedServerLocations =
        (lobbyServerLocationsToDisplay?.length || 0) > 0;
    const hasRequiredQueueSelections =
        hasSelectedMaps && hasSelectedServerLocations;
    const partyMemberUserIds = currentLobbyCode
        ? lobbyMembers.map((m) => m.userId).filter(Boolean)
        : [];

    const mapDisplayNames = lobbyMapsToDisplay
        .map((map) => formatMapName(map))
        .filter(Boolean);
    const knownMapNames = (isWingmanQueue ? AVAILABLE_WINGMAN_MAPS : AVAILABLE_MAPS)
        .map((map) => formatMapName(map));
    const uniqueMapDisplayNames = [...new Set(mapDisplayNames)];
    const mapsRecapSummary = summarizeRecapItems(uniqueMapDisplayNames, {
        total: knownMapNames.length,
        allLabel: "All maps",
        emptyLabel: "No maps",
    });

    const selectedCustomWorkshopMaps = lobbyMapsToDisplay.reduce(
        (acc, selectedMap) => {
            const mapId = normalizeMapSelectionToMapId(selectedMap);
            if (!mapId) return acc;

            const mapMeta = getMapMetaById(mapId);
            const workshopItemId = String(mapMeta?.workshopItemId || "").trim();
            if (!workshopItemId) return acc;
            if (acc.some((item) => item.workshopItemId === workshopItemId)) {
                return acc;
            }

            const mapDisplayName =
                mapMeta?.display || formatMapName(selectedMap || mapId);
            const workshopUrl = buildWorkshopItemUrl(workshopItemId);
            acc.push({
                mapId,
                mapDisplayName,
                workshopItemId,
                workshopUrl,
            });
            return acc;
        },
        [],
    );

    const activeMatchmakingOptionLabels = [];
    if (lobbyOptionsToDisplay.above_hours) {
        const hours = Number(lobbyOptionsToDisplay.above_hours_figure);
        activeMatchmakingOptionLabels.push(
            !Number.isFinite(hours) || hours <= 0 ? "Any playtime" : `${hours}h+`,
        );
    }
    if (lobbyOptionsToDisplay.total_steam_cs_matches_played) {
        const matches = Number(
            lobbyOptionsToDisplay.total_steam_cs_matches_played_figure,
        );
        activeMatchmakingOptionLabels.push(
            !Number.isFinite(matches) || matches <= 0
                ? "Valve any"
                : `Valve ${matches}+`,
        );
    }
    if (lobbyOptionsToDisplay.total_faceit_matches_played) {
        const matches = Number(
            lobbyOptionsToDisplay.total_faceit_matches_played_figure,
        );
        activeMatchmakingOptionLabels.push(
            !Number.isFinite(matches) || matches <= 0
                ? "FaceIT any"
                : `FaceIT ${matches}+`,
        );
    }
    if (lobbyOptionsToDisplay.maxEloDifferenceInTeam) {
        const gap = Number(lobbyOptionsToDisplay.maxEloDifferenceInTeam_figure);
        activeMatchmakingOptionLabels.push(
            !Number.isFinite(gap) || gap <= 0 ? "ELO no limit" : `ELO gap ${gap}`,
        );
    }
    if (lobbyOptionsToDisplay.min_player_age) {
        const age = Number(lobbyOptionsToDisplay.min_player_age_figure);
        activeMatchmakingOptionLabels.push(
            `Age ${Number.isFinite(age) && age > 0 ? age : 16}+`,
        );
    }
    if (lobbyOptionsToDisplay.player_has_inventory) {
        activeMatchmakingOptionLabels.push("Inventory");
    }
    if (lobbyOptionsToDisplay.player_has_expensive_inventory) {
        activeMatchmakingOptionLabels.push("Inv >$500");
    }
    if (lobbyOptionsToDisplay.premium_or_pro_only) {
        activeMatchmakingOptionLabels.push("Premium/Pro");
    }
    if (lobbyOptionsToDisplay.biometric_verified_only) {
        activeMatchmakingOptionLabels.push("Biometric");
    }
    if (
        user?.gender === "female" &&
        lobbyOptionsToDisplay.prefer_female_teammates_if_possible
    ) {
        activeMatchmakingOptionLabels.push("Prefer women");
    }
    if (
        lobbyOptionsToDisplay.play_with_countries &&
        Array.isArray(lobbyOptionsToDisplay.play_with_countries_list) &&
        lobbyOptionsToDisplay.play_with_countries_list.length > 0
    ) {
        const countryCount = lobbyOptionsToDisplay.play_with_countries_list.length;
        activeMatchmakingOptionLabels.push(
            `${countryCount} countr${countryCount === 1 ? "y" : "ies"}`,
        );
    }
    const matchmakingRecapSummary = summarizeRecapItems(
        activeMatchmakingOptionLabels,
        { maxItems: 3, emptyLabel: "No filters" },
    );

    const serverLocationsRecapSummary = useMemo(() => {
        const allLocations = Array.isArray(catalog.serverLocations)
            ? catalog.serverLocations
            : [];
        const selectedNames = lobbyServerLocationsToDisplay
            .map((id) => {
                const loc = allLocations.find((l) => l.id === id);
                return loc ? loc.displayName || loc.name || loc.id : null;
            })
            .filter(Boolean);
        return summarizeRecapItems(selectedNames, {
            total: allLocations.length,
            allLabel: "All regions",
            emptyLabel: "No regions",
        });
    }, [catalog.serverLocations, lobbyServerLocationsToDisplay]);

    const shouldBlockActions = useCallback(() => {
        if (lobbyState === "searching" && !currentLobbyCode) return true;
        if (lobbyState === "searching" && currentLobbyCode && isLeader) return true;
        if (matchmakingStarted && !isLeader && currentLobbyCode) return true;
        return false;
    }, [currentLobbyCode, isLeader, lobbyState, matchmakingStarted]);

    const canFlipCards = useCallback(() => {
        if (lobbyState === "searching" && currentLobbyCode && isLeader)
            return false;
        if (matchmakingStarted && !isLeader && currentLobbyCode) return false;
        return true;
    }, [currentLobbyCode, isLeader, lobbyState, matchmakingStarted]);

    const pendingBySlot = useMemo(() => {
        const map = catalog.inviteSlotAssignments || {};
        const result = {};
        Object.entries(map).forEach(([guestId, slotIndex]) => {
            const friend = (friends || []).find(
                (f) => f.friendData && String(f.friendData.userId) === String(guestId),
            );
            if (friend) {
                result[slotIndex] = {
                    userId: friend.friendData.userId,
                    nickname: friend.friendData.nickname,
                    picture: friend.friendData.picture,
                };
            }
        });
        return result;
    }, [catalog.inviteSlotAssignments, friends]);

    return {
        currentLobbyData,
        isLeader,
        lobbyMembers,
        selfMember,
        hostMember,
        otherMembers,
        isLobbyFull,
        canEditLobbySettings,
        lobbyOptionsToDisplay,
        queueMode,
        lobbyMapsToDisplay,
        lobbyServerLocationsToDisplay,
        hasSelectedMaps,
        hasSelectedServerLocations,
        hasRequiredQueueSelections,
        partyMemberUserIds,
        mapsRecapSummary,
        selectedCustomWorkshopMaps,
        serverLocationsRecapSummary,
        matchmakingRecapSummary,
        shouldBlockActions,
        canFlipCards,
        pendingBySlot,
    };
}
